import React from 'react';
import cn from 'classnames';
import Image from 'next/image';

import {isFontAwesomeIcon} from '../../../shared/utils';
import {focusOnNextInput} from '../../../utils/inputs';

import {BaseState, FocusTypes, InputProps, RenderProps} from './types';
import {getNativeProps} from './utils';

import styles from './styles.module.scss';

/**
 * Based on `text-mask-addons` internal types for render method
 * -> node_modules/@types/react-text-mask/index.d.ts
 *
 * <MaskedInput
 *  render={(ref, renderProps) => (
 *    <input {...renderProps} />
 *  )}
 */

class BaseInput<
  T = InputProps,
  S extends BaseState = BaseState,
> extends React.Component<T & InputProps, S & BaseState> {
  private inputRef: React.RefObject<HTMLInputElement>;

  constructor(props: T & InputProps) {
    super(props);

    this.state = this.getInitialState();
    this.inputRef = React.createRef();
  }

  protected getInitialState() {
    return {
      isValid: true,
      isClicked: false,
    } as BaseState as S;
  }

  getLabel(
    children?: React.ReactElement,
    renderProps?: RenderProps,
  ): React.ReactElement {
    const {id, label, theme, disabled, customLabel} = this.props;

    const disabledStatus = disabled || renderProps?.disabled;

    if (customLabel) {
      return customLabel({children, id, label});
    }

    if (!label) return children;

    const focusType = this.handleFocusStyle();

    return (
      <label
        htmlFor={id}
        className={styles.baseInput__label}
        style={{...renderProps?.theme?.label?.wrap, ...theme?.label?.wrap}}>
        <span
          className={cn(styles.baseInput__label_content, {
            [styles.baseInput__label_content__modError]: focusType === 'error',
            [styles.baseInput__label_content__modFillIn]:
              focusType === 'fill-in',
            [styles.baseInput__label_content__modClicked]:
              focusType === 'clicked',
            [styles.baseInput__label_content__modDisabled]: disabledStatus,
          })}
          style={{
            ...renderProps?.theme?.label?.text,
            ...theme?.label?.text,
          }}>
          {label}
        </span>
        {children}
      </label>
    );
  }

  validState = (status: boolean): void => {
    const {onValidationChange, name} = this.props;

    this.setState({isValid: status});

    onValidationChange?.(status, name);
  };

  wrapperOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const {onChange, onValidationChange, customValidator, name} = this.props;

    onChange?.(event);

    if (customValidator) {
      const isValid = customValidator(event?.target?.value);

      this.setState({isValid});
      onValidationChange?.(isValid, name);
    }
  };

  wrapperOnFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    const {onFocus} = this.props;

    this.setState({isClicked: true});

    onFocus?.(event);
  };

  wrapperKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const {onKeyDown} = this.props;

    if (event.key === 'Tab') {
      event.preventDefault();

      // Logic to tab between inputs
      focusOnNextInput(event.currentTarget);

      return;
    }

    if (event.nativeEvent.code !== 'Enter') {
      return;
    }

    onKeyDown?.(event);
  };

  wrapperKeyUp = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const {onKeyUp} = this.props;

    if (
      event.nativeEvent.code !== 'Enter' &&
      event.nativeEvent.code !== 'Comma' &&
      event.nativeEvent.code !== 'Space'
    ) {
      return;
    }

    onKeyUp?.(event);
  };

  handleFocusStyle = (): FocusTypes => {
    const {hasError, design} = this.props;
    const {isClicked} = this.state;

    if (design === 'fill-in') {
      return 'fill-in';
    }

    if (hasError) {
      return 'error';
    }

    if (isClicked) {
      return 'clicked';
    }

    return undefined;
  };

  renderInput(
    ref?: (inputElement: HTMLElement) => void,
    renderProps?: RenderProps,
  ): React.ReactElement {
    const {
      innerRef,
      testId,
      iconSrc,
      loading,
      disabled,
      iconPosition,
      hasError,
      textAlign,
      design,
      theme,
      maxLength,
      withErrorIcon,
      isReady,
      size,
      className,
      onBlur,
      onClickIcon,
      inputMode,
      autoComplete,
    } = this.props;

    const renderTheme = renderProps?.theme?.input;
    const propsTheme = theme?.input;
    const nativeProps = getNativeProps(this.props);
    const disabledStatus = disabled || renderProps?.disabled;
    const handleClickIcon = renderProps?.onClickIcon || onClickIcon;
    const iconClassName = cn(styles.baseInput__inputWrapper_iconSearch, {
      [styles.baseInput__inputWrapper_iconSearch__modLeftPosition]:
        iconPosition === 'left',
      [styles.baseInput__inputWrapper_iconSearch__modClickable]:
        !!handleClickIcon,
    });
    const iconContent = isFontAwesomeIcon(iconSrc) ? (
      <i className={iconSrc} title="input icon" aria-hidden />
    ) : String(iconSrc).startsWith('/static') ? (
      <img src={iconSrc} alt="input icon" />
    ) : (
      <Image src={iconSrc} alt="input icon" />
    );

    let input = (
      <input
        {...nativeProps}
        className={cn(styles.baseInput__input, {
          [styles.baseInput__input__modRegular]: design === 'regular',
          [styles.baseInput__input__modFillIn]: design === 'fill-in',
          [styles.baseInput__input__modRegular__modLargeSize]:
            size !== 'normal' && design === 'regular',
          [styles.baseInput__input__modDisabled]: disabledStatus,
          [styles.baseInput__input__modTextCenter]: textAlign === 'center',
          [styles.baseInput__input__modTextRight]: textAlign === 'right',
          [styles.baseInput__input__modTextLeft]: textAlign === 'left',
          [styles.baseInput__input__modIconRight]: iconPosition === 'right',
          [styles.baseInput__input__modIconLeft]: iconPosition === 'left',
        })}
        size={size as never}
        disabled={disabledStatus}
        ref={ref || innerRef}
        data-testid={testId}
        maxLength={renderProps?.maxLength || maxLength}
        inputMode={renderProps?.inputMode || inputMode}
        autoComplete={renderProps?.autoComplete || autoComplete || 'on'}
        onChange={renderProps?.onChange || this.wrapperOnChange}
        onFocus={renderProps?.onFocus || this.wrapperOnFocus}
        onKeyUp={this.wrapperKeyUp}
        onKeyDown={renderProps?.onKeyDown || this.wrapperKeyDown}
        onMouseDown={() => {
          this.setState({isClicked: true});
        }}
        type={renderProps?.type}
        onBlur={(e) => {
          this.setState({isClicked: false});

          const incomingHandler = renderProps?.onBlur || onBlur;

          incomingHandler?.(e);
        }}
        style={{...renderTheme?.element, ...propsTheme?.element}}
      />
    );

    if (design === 'fill-in') {
      input = (
        <div
          className={styles.baseInput__fillInWrap}
          style={{...renderTheme?.fillInWrap, ...propsTheme?.fillInWrap}}>
          {input}
          <div
            className={cn(styles.baseInput__fillInWrap_underline, {
              [styles.baseInput__fillInWrap_underline__modError]: hasError,
            })}
            style={{
              ...renderTheme?.fillInUnderline,
              ...propsTheme?.fillInUnderline,
            }}
          />
        </div>
      );
    }

    return (
      <div
        ref={this.inputRef}
        className={cn(
          styles.baseInput__inputWrapper,
          {
            [styles.baseInput__inputWrapper__modRowReverse]:
              !!iconSrc && iconPosition === 'left',
            [styles.baseInput__inputWrapper__modRegular]: design === 'regular',
            [styles.baseInput__inputWrapper__modRegular__modError]: hasError,
            [styles.baseInput__inputWrapper__modRegular__modSizeNormal]:
              size === 'normal' && design === 'regular',
            [styles.baseInput__inputWrapper__modRegular__modSizeMedium]:
              size === 'medium' && design === 'regular',
            [styles.baseInput__inputWrapper__modRegular__modSizeBig]:
              size === 'big' && design === 'regular',
            [styles.baseInput__inputWrapper__modRegular__modDisabled]:
              disabledStatus && design === 'regular',
          },
          className,
        )}
        style={{...renderTheme?.wrap, ...propsTheme?.wrap}}>
        {input}

        {renderProps?.customSlot && renderProps?.customSlot()}

        {/*
          A button only when there is something to click. Without a handler the
          icon is decoration, and rendering it as a button would put an empty
          control in the tab order.
        */}
        {!!iconSrc && !renderProps?.customSlot && !!handleClickIcon && (
          <button
            type="button"
            aria-label="Input action"
            className={iconClassName}
            onClick={handleClickIcon}>
            {iconContent}
          </button>
        )}

        {!!iconSrc && !renderProps?.customSlot && !handleClickIcon && (
          <span className={iconClassName}>{iconContent}</span>
        )}

        {loading ? (
          <div
            className={styles.baseInput__loaderWrap}
            style={{
              ...renderTheme?.loader,
              ...propsTheme?.loader,
            }}>
            <div
              className={cn(styles.baseInput__loaderWrap_loader, {
                [styles.baseInput__loaderWrap_loader__modBlue]:
                  design === 'fill-in',
                [styles.baseInput__loaderWrap_loader__modGray]:
                  design === 'regular',
              })}
            />
          </div>
        ) : (
          <>
            {isReady && (
              <span
                className={cn(styles.baseInput__inputWrapper_isReady, {
                  [styles.baseInput__inputWrapper_isReady____modLargeSize]:
                    size !== 'normal' && design === 'regular',
                })}
                style={{
                  ...renderTheme?.isReadyIcon,
                  ...propsTheme?.isReadyIcon,
                }}>
                <i
                  className="fa-solid fa-circle-check"
                  title="input is ready icon"
                  data-testid="input-ready-icon"
                />
              </span>
            )}

            {withErrorIcon && hasError && (
              <div
                className={styles.baseInput__inputWrapper_iconError}
                style={{
                  ...renderTheme?.hasErrorIcon,
                  ...propsTheme?.hasErrorIcon,
                }}>
                <i
                  className="fa-solid fa-circle-exclamation"
                  title="input error icon"
                  data-testid="input-error-icon"
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  renderBaseComponent(
    ref?: (inputElement: HTMLElement) => void,
    renderProps?: RenderProps,
  ): React.ReactElement {
    const {label, theme, errorMessage, hasError, hintText, optional, design} =
      this.props;

    const hasErrorMsg = errorMessage || renderProps?.errorMessage;

    const {isValid} = this.state;

    return (
      <div
        className={styles.baseInput__container}
        style={{...renderProps?.theme?.container, ...theme?.container}}>
        {label?.length
          ? this.getLabel(this.renderInput(ref, renderProps), renderProps)
          : this.renderInput(ref, renderProps)}
        <div
          className={cn(styles.baseInput__hintContainer, {
            [styles.baseInput__hintContainer__modHidden]:
              !hintText && !hasErrorMsg && !optional && design === 'fill-in',
          })}>
          {hasErrorMsg && (hasError || !isValid) && (
            <div
              className={styles.baseInput__hintContainer_error}
              style={{
                ...renderProps?.theme?.errorMessage,
                ...theme?.errorMessage,
              }}>
              {errorMessage || renderProps.errorMessage}
            </div>
          )}

          {!hasError && hintText && (
            <div
              className={styles.baseInput__hintContainer_hint}
              style={{
                ...renderProps?.theme?.hintMessage,
                ...theme?.hintMessage,
              }}>
              {hintText}
            </div>
          )}

          {optional && (
            <div className={styles.baseInput__hintContainer_optionalMessage}>
              Optional
            </div>
          )}
        </div>
      </div>
    );
  }

  render(): React.ReactElement {
    return this.renderBaseComponent();
  }

  static defaultProps: InputProps = {
    id: undefined,
    innerRef: undefined,
    label: undefined,
    design: 'regular',
    testId: undefined,
    loading: false,
    theme: null,
    iconSrc: undefined,
    iconPosition: undefined,
    hintText: undefined,
    optional: false,
    hasError: false,
    disabled: false,
    textAlign: undefined,
    errorMessage: undefined,
    value: undefined,
    name: undefined,
    customLabel: undefined,
    customValidator: undefined,
    maxLength: undefined,
    onClickIcon: null,
    onValidationChange: null,
    size: 'normal',
  };
}

export default BaseInput;
